import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Coffee, GraduationCap, Users, FlaskConical, Building2, Wrench, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Eyes } from "@/components/BrandMark";
import { useLang } from "@/i18n";
const ICONS = [Coffee, GraduationCap, Users, FlaskConical, Building2, Wrench];
const PHOTO_ALT = [
  "Barista preparando un espresso en la barra",
  "Taller de latte art: jarra vertiendo leche sobre un café",
  "Mesa de cata con cafés de especialidad",
];
const PHOTOS = [
  "https://images.unsplash.com/photo-1517701848373-805868efd0ac",
  "https://images.pexels.com/photos/25841800/pexels-photo-25841800.jpeg",
  "https://images.unsplash.com/photo-1558996260-4bac67dbd110",
];

// Both CDNs resize (and serve webp) from query params, so one source covers every screen.
const photoSrc = (url, w) =>
  url.includes("unsplash.com")
    ? `${url}?auto=format&fit=crop&q=70&w=${w}`
    : `${url.split("?")[0]}?auto=compress&cs=tinysrgb&w=${w}`;
const photoSrcSet = (url) => [400, 600, 800, 1000].map((w) => `${photoSrc(url, w)} ${w}w`).join(", ");

export const MoreThanCoffee = () => {
  const { t } = useLang();
  return (
    <section className="bg-[#121212] py-16 md:py-20 px-6 md:px-12 lg:px-20">
      <div className="max-w-5xl mx-auto text-center">
        <Reveal>
          <div className="flex justify-center mb-6">
            <Eyes size={16} color="#F5F1E8" gap={14} blink />
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#F5F1E8] leading-snug">
            {t.academia.highlight}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/formacion"
            data-testid="more-academia-btn"
            className="inline-flex items-center gap-2 mt-8 min-h-[48px] bg-[#F4D35E] text-[#3B2523] font-semibold px-8 py-3.5 rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
          >
            {t.academia.title}
            <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const Academia = () => {
  const { t } = useLang();
  const a = t.academia;

  return (
    <section id="academia" className="bg-[#F5F1E8] py-24 md:py-32 px-6 md:px-12 lg:px-20 scroll-mt-[104px]">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <Eyes size={13} color="#3B2523" gap={11} blink />
            <p className="text-[#3B2523]/70 uppercase tracking-[0.25em] text-xs font-semibold">{a.tag}</p>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#3B2523]">
            {a.title}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-[#3B2523]/70 text-lg max-w-2xl mt-6 leading-relaxed">{a.intro}</p>
        </Reveal>

        {/* Photo strip */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mt-12">
          {PHOTOS.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="overflow-hidden rounded-2xl aspect-[4/5] group"
            >
              <img
                src={photoSrc(p, 800)}
                srcSet={photoSrcSet(p)}
                sizes="(max-width: 768px) 33vw, 30vw"
                alt={PHOTO_ALT[i]}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </motion.div>
          ))}
        </div>

        {/* Courses */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {a.courses.map((c, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={i}
                data-testid={`course-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-white rounded-2xl p-7 border border-[#3B2523]/10 flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-[#3B2523] flex items-center justify-center">
                  <Icon size={22} className="text-[#F4D35E]" strokeWidth={1.7} />
                </div>
                <h3 className="font-display text-xl font-bold text-[#3B2523] mt-5">{c.t}</h3>
                <p className="text-[#3B2523]/70 text-sm mt-2 leading-relaxed flex-1">{c.d}</p>
                <Link
                  to="/formacion#formulario"
                  data-testid={`course-cta-${i}`}
                  className="self-start inline-flex items-center gap-2 mt-5 min-h-[44px] text-[#3B2523] font-semibold text-sm hover:gap-3 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B2523] focus-visible:ring-offset-2 rounded"
                >
                  {a.cta1}
                  <ArrowRight size={15} />
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* CTA band */}
        <div className="mt-12 bg-[#3B2523] rounded-3xl p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-display text-2xl md:text-3xl font-bold text-[#F5F1E8] max-w-md">
            {a.highlight}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/formacion#formulario"
              data-testid="academia-reservar"
              className="inline-flex items-center min-h-[48px] bg-[#F4D35E] text-[#3B2523] font-semibold px-7 py-3.5 rounded-full transition-all hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#3B2523]"
            >
              {a.cta2}
            </Link>
            <Link
              to="/contacto"
              data-testid="academia-presupuesto"
              className="inline-flex items-center min-h-[48px] border border-[#F5F1E8]/40 text-[#F5F1E8] font-semibold px-7 py-3.5 rounded-full transition-all hover:bg-[#F5F1E8] hover:text-[#3B2523] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#3B2523]"
            >
              {a.cta3}
            </Link>
          </div>
        </div>

        <p className="text-[#3B2523]/70 text-sm mt-8 max-w-3xl">{a.seo}</p>
      </div>
    </section>
  );
};

export default Academia;
